<template>
  <CheckoutStep>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="availableItems"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <template #header>
      <Stepper
        active-step="addresses"
        @back="
          navigateTo({ name: CHECKOUT.IN_STORE_SALES.BOUYGUES_TELECOM.CART })
        "
      />
    </template>

    <div>
      <Transition
        appear
        enter-active-class="transition-opacity duration-300 ease-in-out"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-300 ease-in-out"
        leave-to-class="opacity-0"
      >
        <div v-if="isEdition">
          <Suspense>
            <ShippingAddress
              :billable-countries
              :items
              :loading
              :shippable-countries
              :shipping-address="initialAddress"
              :swap-offer
              @update="handleShippingAddressUpdate"
            />
            <template #fallback>
              <RevLoadingScreen class="p-80" text="" />
            </template>
          </Suspense>
        </div>

        <DeliveryRecap
          v-else
          :items
          :last-collection-point
          :loading
          @submit="handleNextStep"
          @update="emit('update', $event)"
        >
          <RevCard
            v-if="props.addressList.deliver"
            class="flex flex-col items-start justify-between gap-5 p-24 lg:flex-row lg:items-center"
          >
            <Address
              :address="props.addressList.deliver"
              :with-details="!props.addressList.deliverCollectionPoint"
              with-phone
            />

            <RevButton
              :icon="IconEdit"
              size="small"
              type="button"
              variant="secondary"
              @click="isEdition = true"
            >
              {{ i18n(translations.editButton) }}
            </RevButton>
          </RevCard>
        </DeliveryRecap>
      </Transition>
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import Address from '@backmarket/nuxt-module-address/Address.vue'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconEdit } from '@ds/icons/IconEdit'

import type { CartItem, CartSummary } from '~/api/cart/types'
import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import ShippingAddress from '~/scopes/checkout/components/ShippingAddress/ShippingAddress.vue'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'

import Stepper from '../../components/Stepper/Stepper.vue'

import translations from './AddressesStep.translations'
import DeliveryRecap from './components/DeliveryRecap/DeliveryRecap.vue'

defineOptions({
  // Prevents unused props passed by NuxtPage from being added as DOM attributes
  inheritAttrs: false,
})

type AddressList = ApiSpecs['/bm/cart']['GET']['response']['addressList']

const props = defineProps<{
  loading: boolean
  items: CartItem[]
  addressList: AddressList
  initialAddress: AddressList['deliver']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  summary: CartSummary
  lastCollectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
}>()

const emit = defineEmits<{
  (e: 'update', callback?: () => void): void
}>()

const { trackFunnel } = useCheckoutTracking()
const tracking = useTracking()
const i18n = useI18n()

const availableItems = computed(() => getAvailableItems(props.items))

const isEdition = ref<boolean>(true)

function handleShippingAddressUpdate() {
  emit('update', () => {
    isEdition.value = false
    tracking.trackClick({
      name: 'shipping-address-updated',
      zone: 'bouygues-in-store-checkout',
    })
  })
}

async function handleNextStep() {
  await navigateTo({
    name: CHECKOUT.IN_STORE_SALES.BOUYGUES_TELECOM.PAYMENT,
  })
}

onMounted(async () => {
  trackFunnel(props.items, props.swapOffer, 'bouygues-in-store-addresses')
})
</script>
